import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n/hooks';
import { PageContainer, ArbiWinHeader, ArbiWinFooter, MainContainer, Button, Card } from '../../ui';
import { updateRouteMeta } from '../../navigation/utils/routeUtils';
import './StaticPages.css';

/**
 * StaticPages - Component for displaying static pages (Terms, Privacy, Contact, About, Subscribe)
 * 
 * Features:
 * - Terms of Service page with legal content
 * - Privacy Policy page with GDPR compliance
 * - Contact Us page with contact form
 * - "Who We Are" page with company information
 * - "How to Subscribe" page with pricing
 * - Responsive design and proper typography
 */
const StaticPages = ({ 
  isAuthenticated = false, 
  user = null, 
  onLogout = null, 
  onProfile = null,
  className = '' 
}) => {
  const { page } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activePage, setActivePage] = useState(page || 'terms');

  // Update active page when route param changes
  useEffect(() => {
    if (page) {
      setActivePage(page);
    }
  }, [page]);

  // Update SEO meta tags
  useEffect(() => {
    const pageMeta = {
      terms: { title: t('static.terms.title'), description: t('static.terms.description') },
      privacy: { title: t('static.privacy.title'), description: t('static.privacy.description') },
      contact: { title: t('static.contact.title'), description: t('static.contact.description') },
      about: { title: t('static.about.title'), description: t('static.about.description') },
      subscribe: { title: t('static.subscribe.title'), description: t('static.subscribe.description') }
    };

    const meta = pageMeta[activePage];
    if (meta) {
      document.title = meta.title;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', meta.description);
      }
    }
  }, [activePage, t]);

  // Render appropriate page component
  const renderPage = () => {
    switch (activePage) {
      case 'terms':
        return <TermsPage />;
      case 'privacy':
        return <PrivacyPage />;
      case 'contact':
        return <ContactPage />;
      case 'about':
        return <AboutPage />;
      case 'subscribe':
        return <SubscribePage />;
      default:
        return <TermsPage />;
    }
  };

  return (
    <PageContainer>
      <ArbiWinHeader
        isAuthenticated={isAuthenticated}
        user={user}
        onLogin={() => navigate('/login')}
        onRegister={() => navigate('/register')}
        onLogout={onLogout}
        onProfile={onProfile}
        onHomeClick={() => navigate('/')}
      />
      <MainContainer>
        <div className={`static-pages ${className}`}>
          <div className="static-pages__container">
            <div className="static-pages__navigation">
              <button
                className={`static-pages__nav-item ${activePage === 'terms' ? 'active' : ''}`}
                onClick={() => navigate('/static/terms')}
              >
                {t('static.nav.terms')}
              </button>
              <button
                className={`static-pages__nav-item ${activePage === 'privacy' ? 'active' : ''}`}
                onClick={() => navigate('/static/privacy')}
              >
                {t('static.nav.privacy')}
              </button>
              <button
                className={`static-pages__nav-item ${activePage === 'contact' ? 'active' : ''}`}
                onClick={() => navigate('/static/contact')}
              >
                {t('static.nav.contact')}
              </button>
              <button
                className={`static-pages__nav-item ${activePage === 'about' ? 'active' : ''}`}
                onClick={() => navigate('/static/about')}
              >
                {t('static.nav.about')}
              </button>
              <button
                className={`static-pages__nav-item ${activePage === 'subscribe' ? 'active' : ''}`}
                onClick={() => navigate('/static/subscribe')}
              >
                {t('static.nav.subscribe')}
              </button>
            </div>

            <div className="static-pages__content">
              {renderPage()}
            </div>
          </div>
        </div>
      </MainContainer>
      <ArbiWinFooter />
    </PageContainer>
  );
};

/**
 * TermsPage - Terms of Service page
 */
const TermsPage = () => {
  const { t } = useTranslation();

  // Helper function to render content with newlines
  const renderContent = (content) => {
    if (!content) return null;
    return content.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < content.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <Card className="static-page">
      <h1 className="static-page__title">{t('static.terms.title')}</h1>
      <div className="static-page__content">
        {t('static.terms.companyInfo') && (
          <div className="static-page__company-info">
            <pre className="static-page__company-info-text">{t('static.terms.companyInfo')}</pre>
          </div>
        )}

        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map((num) => (
          <section key={num} className="static-page__section">
            <h2 className="static-page__section-title">{t(`static.terms.section${num}.title`)}</h2>
            <p className="static-page__section-content">{renderContent(t(`static.terms.section${num}.content`))}</p>
          </section>
        ))}
      </div>
    </Card>
  );
};

const COOKIE_CONSENT_OPEN_SETTINGS_EVENT = 'cookie-consent:openSettings';
const COOKIE_SETTINGS_LINK_PLACEHOLDER = '{{COOKIE_SETTINGS_LINK}}';

/**
 * PrivacyPage - Privacy Policy page with GDPR compliance
 */
const PrivacyPage = () => {
  const { t } = useTranslation();

  const openCookieSettings = () => {
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_OPEN_SETTINGS_EVENT));
  };

  // Helper function to render content with newlines
  const renderContent = (content) => {
    if (!content) return null;
    return content.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < content.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const renderSectionContent = (content, sectionNum) => {
    if (!content) return null;
    if (sectionNum === 5 && content.includes(COOKIE_SETTINGS_LINK_PLACEHOLDER)) {
      const parts = content.split(COOKIE_SETTINGS_LINK_PLACEHOLDER);
      const before = parts[0];
      const after = parts.slice(1).join(COOKIE_SETTINGS_LINK_PLACEHOLDER);
      const link = (
        <button
          type="button"
          className="static-page__footer-link static-page__section-content-link"
          onClick={openCookieSettings}
        >
          {t('footer.cookieSettings')}
        </button>
      );
      return (
        <>
          {renderContent(before)}
          {link}
          {renderContent(after)}
        </>
      );
    }
    return renderContent(content);
  };

  return (
    <Card className="static-page">
      <h1 className="static-page__title">{t('static.privacy.title')}</h1>
      <div className="static-page__content">
        {t('static.privacy.companyInfo') && (
          <div className="static-page__company-info">
            <pre className="static-page__company-info-text">{t('static.privacy.companyInfo')}</pre>
          </div>
        )}

        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map((num) => (
          <section key={num} className="static-page__section">
            <h2 className="static-page__section-title">{t(`static.privacy.section${num}.title`)}</h2>
            <p className="static-page__section-content">{renderSectionContent(t(`static.privacy.section${num}.content`), num)}</p>
          </section>
        ))}
      </div>
    </Card>
  );
};

/**
 * ContactPage - Contact Us page with support/legal contacts, instructions and footer
 */
const ContactPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Card className="static-page">
      <h1 className="static-page__title">{t('static.contact.title')}</h1>
      <div className="static-page__content">
        <section className="static-page__section">
          <p className="static-page__intro">{t('static.contact.intro')}</p>
        </section>

        <section className="static-page__section">
          <h2 className="static-page__section-title">{t('static.contact.section1.title')}</h2>
          <p>{t('static.contact.section1.description')}</p>
          <p className="contact-email__address">
            <strong>{t('static.contact.section1.emailLabel')}: </strong>
            <a href="mailto:support@arbiwin.bg" className="contact-email__link">
              {t('static.contact.section1.email')}
            </a>
          </p>
        </section>

        <section className="static-page__section">
          <h2 className="static-page__section-title">{t('static.contact.section2.title')}</h2>
          <p>{t('static.contact.section2.description')}</p>
          <p className="contact-email__address">
            <strong>{t('static.contact.section2.emailLabel')}: </strong>
            <a href="mailto:legal@arbiwin.bg" className="contact-email__link">
              {t('static.contact.section2.email')}
            </a>
          </p>
        </section>

        <section className="static-page__section">
          <h2 className="static-page__section-title">{t('static.contact.instructions.title')}</h2>
          <p>{t('static.contact.instructions.intro')}</p>
          <ul className="static-page__list">
            <li>{t('static.contact.instructions.item1')}</li>
            <li>{t('static.contact.instructions.item2')}</li>
          </ul>
        </section>

        <section className="static-page__section">
          <h2 className="static-page__section-title">{t('static.contact.response.title')}</h2>
          <p>{t('static.contact.response.time')}</p>
        </section>

        <footer className="static-page__footer">
          <span>{t('static.contact.footer.company')}</span>
          <span> | </span>
          <span>{t('static.contact.footer.address')}</span>
          <span> | </span>
          <button
            type="button"
            className="static-page__footer-link"
            onClick={() => navigate('/static/terms')}
          >
            {t('static.contact.footer.terms')}
          </button>
          <span> | </span>
          <button
            type="button"
            className="static-page__footer-link"
            onClick={() => navigate('/static/privacy')}
          >
            {t('static.contact.footer.privacy')}
          </button>
        </footer>
      </div>
    </Card>
  );
};

/**
 * AboutPage - "Who We Are" page with company information
 */
const AboutPage = () => {
  const { t } = useTranslation();

  return (
    <Card className="static-page">
      <h1 className="static-page__title">{t('static.about.title')}</h1>
      <div className="static-page__content">
        <section className="static-page__section">
          <h2 className="static-page__section-title">{t('static.about.section1.title')}</h2>
          <p>{t('static.about.section1.content')}</p>
        </section>

        <section className="static-page__section">
          <h2 className="static-page__section-title">{t('static.about.section2.title')}</h2>
          <p>{t('static.about.section2.content')}</p>
        </section>

        <section className="static-page__section">
          <h2 className="static-page__section-title">{t('static.about.section3.title')}</h2>
          <p>{t('static.about.section3.content')}</p>
          <ul className="static-page__list">
            <li>{t('static.about.section3.item1')}</li>
            <li>{t('static.about.section3.item2')}</li>
            <li>{t('static.about.section3.item3')}</li>
            <li>{t('static.about.section3.item4')}</li>
          </ul>
        </section>

        <section className="static-page__section">
          <h2 className="static-page__section-title">{t('static.about.section4.title')}</h2>
          <p>{t('static.about.section4.content')}</p>
        </section>

        <section className="static-page__section">
          <h2 className="static-page__section-title">{t('static.about.section5.title')}</h2>
          <p>{t('static.about.section5.content')}</p>
          <p>
            <a href="mailto:support@arbiwin.bg" className="contact-email__link">
              {t('static.about.section5.supportEmail')}
            </a>
          </p>
        </section>
      </div>
    </Card>
  );
};

/**
 * SubscribePage - "How to Subscribe" page with pricing
 */
const SubscribePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Card className="static-page">
      <h1 className="static-page__title">{t('static.subscribe.title')}</h1>
      <div className="static-page__content">
        <section className="static-page__section">
          <h2 className="static-page__section-title">{t('static.subscribe.section1.title')}</h2>
          <p>{t('static.subscribe.section1.content')}</p>
        </section>

        <section className="static-page__section">
          <h2 className="static-page__section-title">{t('static.subscribe.section2.title')}</h2>
          <div className="subscribe-pricing">
            <div className="subscribe-pricing__card">
              <h3 className="subscribe-pricing__title">{t('static.subscribe.pricing.title')}</h3>
              <div className="subscribe-pricing__price">
                <span className="subscribe-pricing__amount">
                  {t('static.subscribe.pricing.amount')} <span className="subscribe-pricing__period">{t('static.subscribe.pricing.period')}</span>
                </span>
              </div>
              <div className="subscribe-pricing__promo">
                <p className="subscribe-pricing__promo-text">{t('static.subscribe.pricing.promoText')}</p>
                <p className="subscribe-pricing__promo-disclaimer">{t('static.subscribe.pricing.promoDisclaimer')}</p>
              </div>
              <h4 className="subscribe-pricing__features-heading">{t('static.subscribe.pricing.featuresIncludes')}</h4>
              <ul className="subscribe-pricing__features">
                <li>{t('static.subscribe.pricing.feature1')}</li>
                <li>{t('static.subscribe.pricing.feature2')}</li>
                <li>{t('static.subscribe.pricing.feature3')}</li>
                <li>{t('static.subscribe.pricing.feature4')}</li>
                <li>{t('static.subscribe.pricing.feature5')}</li>
              </ul>
              <Button
                variant="primary"
                size="large"
                onClick={() => navigate('/register')}
                className="subscribe-pricing__button"
              >
                {t('static.subscribe.pricing.button')}
              </Button>
            </div>
          </div>
        </section>

        <section className="static-page__section">
          <h2 className="static-page__section-title">{t('static.subscribe.section3.title')}</h2>
          <ol className="static-page__ordered-list">
            <li>{t('static.subscribe.steps.step1')}</li>
            <li>{t('static.subscribe.steps.step2')}</li>
            <li>{t('static.subscribe.steps.step3')}</li>
            <li>{t('static.subscribe.steps.step4')}</li>
            <li>{t('static.subscribe.steps.step5')}</li>
          </ol>
        </section>

        <section className="static-page__section">
          <h2 className="static-page__section-title">{t('static.subscribe.section4.title')}</h2>
          <h3 className="static-page__subsection-title">{t('static.subscribe.section4.faqQuestion')}</h3>
          <p>{t('static.subscribe.section4.content')}</p>
          <h3 className="static-page__subsection-title">{t('static.subscribe.section4.faqQuestion2')}</h3>
          <p>{t('static.subscribe.section4.content2')}</p>
        </section>

        <section className="static-page__section">
          <h2 className="static-page__section-title">{t('static.subscribe.section5.title')}</h2>
          <p>{t('static.subscribe.section5.content')}</p>
          <p>
            <a href="mailto:support@arbiwin.bg" className="contact-email__link">
              {t('static.subscribe.section5.supportEmail')}
            </a>
          </p>
        </section>

        <footer className="static-page__footer">
          <span>{t('static.subscribe.footer.company')}</span>
          <span> | </span>
          <span>{t('static.subscribe.footer.address')}</span>
          <span> | </span>
          <button
            type="button"
            className="static-page__footer-link"
            onClick={() => navigate('/static/terms')}
          >
            {t('static.subscribe.footer.terms')}
          </button>
          <span> | </span>
          <button
            type="button"
            className="static-page__footer-link"
            onClick={() => navigate('/static/privacy')}
          >
            {t('static.subscribe.footer.privacy')}
          </button>
        </footer>
      </div>
    </Card>
  );
};

StaticPages.propTypes = {
  isAuthenticated: PropTypes.bool,
  user: PropTypes.object,
  onLogout: PropTypes.func,
  onProfile: PropTypes.func,
  className: PropTypes.string
};

export default StaticPages;

